import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';

import { useAuthenticatedUser } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Avatar,
	Badge,
	Heading3,
	Paragraph,
	Pill,
	PlainCard,
	Popover,
	Skeleton,
} from '@treams/treams-ui';

import SpontaneousFeedbackModal from './SpontaneousFeedbackModal';

export default Skeleton(
	function IncomingFeedbackCard(props: { request: graphql.FeedbackRequest; userID: string }) {
		const { t } = useTranslation();
		const viewer = useAuthenticatedUser();
		const history = useHistory();

		const [warning, setWarning] = useState(false);

		const numberOfRecipients = props.request.responses.edges.length;
		const hasRecipientsOverflow = numberOfRecipients > 4;

		const recipients = props.request.responses.edges.slice(0, hasRecipientsOverflow ? 3 : 4);

		const url =
			viewer.id === props.userID
				? `/feedback/${props.request.id}`
				: `/users/${props.userID}/feedback/${props.request.id}`;

		return (
			<>
				<Popover center backdrop visible={warning} onDismiss={() => setWarning(false)}>
					<SpontaneousFeedbackModal
						request={props.request}
						onDismiss={() => setWarning(false)}
						onContinue={() => history.push(url)}
					/>
				</Popover>
				<PlainCard
					onClick={
						props.request.mode === graphql.FeedbackMode.Spontaneous
							? () => setWarning(true)
							: undefined
					}
					to={props.request.mode === graphql.FeedbackMode.Spontaneous ? undefined : url}
				>
					<Flex flexDirection="column" alignItems="flex-start" style={{ gap: 12 }}>
						<Box>
							<Heading3>{props.request.title || t('feedback.incoming.card.title')}</Heading3>
							<Paragraph mt="4px">
								{t('feedback.incoming.card.createdAt', {
									date: moment(props.request.createdAt).format('LL'),
								})}
								{' · '}
								{t('feedback.incoming.card.dueDate', {
									date: moment(props.request.dueDate).format('LL'),
								})}
								{' · '}
								{t('feedback.incoming.card.progress', {
									shared: props.request.responses.edges.filter(
										({ node }) => node.status === graphql.FeedbackResponseStatus.Shared,
									).length,
									total: props.request.responses.edges.length,
								})}
							</Paragraph>
						</Box>
						<Flex alignItems="center" style={{ gap: 12 }}>
							{props.request.mode === graphql.FeedbackMode.Spontaneous && (
								<Pill>{t('feedback.incoming.card.spontaneous')}</Pill>
							)}
							{recipients.map(({ node }) => (
								<Badge.Container
									key={node.id}
									badge={
										node.status === graphql.FeedbackResponseStatus.Shared ? (
											<Badge.Success />
										) : (
											<Badge.Pending />
										)
									}
								>
									<Avatar size={36} user={node.recipient} />
								</Badge.Container>
							))}

							{hasRecipientsOverflow && (
								<Avatar.Overflow size={36} count={numberOfRecipients - 3} />
							)}
						</Flex>
					</Flex>
				</PlainCard>
			</>
		);
	},
	function IncomingFeedbackCardSkeleton() {
		return (
			<PlainCard.Skeleton>
				<Box>
					<Heading3.Skeleton />
					<Paragraph.Skeleton mt="4px" lines={1} />
				</Box>
			</PlainCard.Skeleton>
		);
	},
);
